import {useState} from "react";
import {useValidatedPromiseState, useValidation} from "../../../../hooks/validationState";
import {withDialog} from "../../../../state/withDialog";
import {DialogBody, DialogFooter, DialogHeader, DialogTitle} from "../../../../components/ui/dialog";
import {HStack, Input, Stack, Text} from "@chakra-ui/react";
import {Button} from "../../../../components/ui/button";
import {Field} from "../../../../components/ui/field";
import {AccountMin, ThirdParty, ThirdPartyId, Portal} from "platformed-client/types";
import {Alert} from "../../../../components/ui/alert";
import api from "../../../../api";
import externalApi from "../../../../api/external";
import {unreachableCase} from "../../../../utils/typescript";
import {q, useQueryData} from "../../../../state";
import {withSuspense} from "../../../../state/withSuspense";
import T from "platformed-components/i18n/T";

type Options = {type: "tprm"; thirdParty: ThirdParty} | {type: "collect"; account: AccountMin; portal: Portal};

/** Warns that the portal has nothing for the invited user to work on. Renders nothing otherwise. */
const EmptyPortalWarning = withSuspense(
  ({thirdPartyId}: {thirdPartyId: ThirdPartyId}) => {
    const engagements = useQueryData(q.tprm.thirdPartyEngagements(thirdPartyId));

    const hasClientRequests = engagements.some(engagement =>
      engagement.external_questionnaires.some(questionnaire => questionnaire.outstanding_for_client),
    );

    if (hasClientRequests) {
      return null;
    }

    return (
      <Alert status="warning" title={<T keyName="c.No_client_requests_yet" />}>
        <T keyName="tprm.portal.invite_dialog.no_client_requests_warning" />
      </Alert>
    );
  },
  <></>,
);

export const InvitePortalUserDialog = withDialog(({options, onClose}: {options: Options; onClose: () => void}) => {
  const [email, setEmail] = useState("");

  const [validationErrors, setValidationErrors] = useValidation(useState({}), {email});

  const [inviting, invite] = useValidatedPromiseState(
    async () => {
      const trimmedEmail = email.trim();
      switch (options.type) {
        case "tprm": {
          await api.tprm.thirdParties.invitePortalUser(options.thirdParty.third_party_id, {email: trimmedEmail});
          break;
        }
        case "collect": {
          await externalApi.portal.invitePortalUser(options.account, options.portal.third_party_id, [
            {email: trimmedEmail},
          ]);
          break;
        }
        default:
          unreachableCase(options);
      }
      onClose();
    },
    [email, onClose, options],
    setValidationErrors,
  );

  return (
    <form onSubmit={invite}>
      <DialogHeader>
        <DialogTitle>
          <T keyName="c.Invite_new_user" />
        </DialogTitle>
      </DialogHeader>
      <DialogBody>
        <Stack>
          {options.type === "tprm" && <EmptyPortalWarning thirdPartyId={options.thirdParty.third_party_id} />}
          <Field
            label={<T keyName="c.Email_address" />}
            required
            invalid={validationErrors.email !== undefined}
            errorText={validationErrors.email && validationErrors.email[0] && validationErrors.email[0].message}
          >
            <Input value={email} onChange={ev => setEmail(ev.target.value)} />
          </Field>
          <Text color="fg.muted">
            {options.type === "tprm" && (
              <T keyName="tprm.portal.invite_dialog.description_tprm" params={{name: options.thirdParty.name}} />
            )}
            {options.type === "collect" && <T keyName="tprm.portal.invite_dialog.description_collect" />}
          </Text>

          {inviting.lastError != null && (
            <Alert status="error" title={<T keyName="c.Error_inviting_user" />}>{`${inviting.lastError}`}</Alert>
          )}
        </Stack>
      </DialogBody>

      <DialogFooter>
        <HStack gap="3">
          <Button variant="ghost" onClick={onClose} disabled={inviting.inProgress}>
            <T keyName="c.Cancel" />
          </Button>
          <Button colorPalette="green" type="submit" disabled={inviting.inProgress}>
            <T keyName="c.Send_invitation" />
          </Button>
        </HStack>
      </DialogFooter>
    </form>
  );
});
